import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Section174AdjAllocationsService } from './section174-adj-allocations.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-section174-adj-allocations',
    templateUrl: './section174-adj-allocations.component.html',
    styleUrls: ['./section174-adj-allocations.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Section174AdjAllocationsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  sectionLoading = true;
  noRecords = false;

  // Tab management
  activeTab = 0;

  // Data properties from inputs
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  filterParams: any = {};

  // Form data
  section174adjList: any[] = [];
  sic_code_all: any[] = [];
  sic_code_display: any[] = [];
  in_out_all: any[] = [];
  in_out_display: any[] = [];

  // Modal title
  modalTitle = 'Section 174 Allocation';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private section174Service: Section174AdjAllocationsService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = {
      globalParams: header.globalParams || {},
      taxYearInputs: (header as any).taxYearInputs || []
    };
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    const hasGlobalService = this.globalService && this.globalService.globalParams;
    const hasGeneralConfig = this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url;
    const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;

    if (hasGlobalService && hasGeneralConfig && hasRowData && hasFilterParams) {
      this.isInitialized = true;
      this.init();
    }
  }

  init() {
    try {
      this.sectionLoading = true;
      this.crudLoading = false;
      this.section174adjList = [];
      this.noRecords = false;

      // Load all data
      this.getModalSection174();
      this.getSicCode();
      this.getInOutData();
    } catch (e) {
      console.error('[Section174] Error in init:', e);
      this.sectionLoading = false;
      this.cdr.detectChanges();
    }
  }

  // Load IN/OUT dropdown data
  getInOutData() {
    const specificParams = {
      action_code: 'ucyo2g',
      combi_key: this.rowData.COMBINATION_KEY
    };

    this.section174Service.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      specificParams,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          return;
        }

        this.in_out_all = [];
        this.in_out_display = [];
        this.in_out_all = data.jsonObject || [];

        // Add empty option at the beginning for "no selection"
        this.in_out_display.push({
          label: '',
          value: null
        });

        for (let cc in this.in_out_all) {
          this.in_out_display.push({
            label: this.in_out_all[cc].IN_OUT_FLAG,
            value: this.in_out_all[cc].IN_OUT_FLAG
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[Section174] Error loading IN/OUT data:', error);
      }
    );
  }

  // Load SIC Code dropdown data
  getSicCode() {
    const specificParams = {
      action_code: '8y8czx',
      combi_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.CHART_KEY
    };

    this.section174Service.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      specificParams,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          return;
        }

        this.sic_code_all = [];
        this.sic_code_display = [];
        this.sic_code_all = data.jsonObject || [];

        // Add empty option at the beginning for "no selection"
        this.sic_code_display.push({
          label: '',
          value: null
        });

        for (let c in this.sic_code_all) {
          this.sic_code_display.push({
            label: this.sic_code_all[c].SIC_CODE_DESC,
            value: this.sic_code_all[c].SIC_CODE_DESC
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[Section174] Error loading SIC codes:', error);
      }
    );
  }

  // Load main form data
  getModalSection174() {
    this.sectionLoading = true;
    const specificParams = {
      action_code: 'rsj4du',
      combi_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.CHART_KEY,
      acct_key: this.rowData.ACCT_KEY
    };

    this.section174Service.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      specificParams,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.sectionLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.section174adjList = [];
        this.section174adjList = data.jsonObject || [];

        if (this.section174adjList.length === 0) {
          this.noRecords = true;
        } else {
          this.noRecords = false;
          // Process each row to add tracking properties
          for (let dd in this.section174adjList) {
            // Normalize empty strings to null for dropdown binding
            if (this.section174adjList[dd].SIC_CODE === '' || this.section174adjList[dd].SIC_CODE === undefined) {
              this.section174adjList[dd].SIC_CODE = null;
            }
            if (this.section174adjList[dd].IN_OUT_174 === '' || this.section174adjList[dd].IN_OUT_174 === undefined) {
              this.section174adjList[dd].IN_OUT_174 = null;
            } else if (this.section174adjList[dd].IN_OUT_174) {
              // Normalize IN_OUT_174 to match dropdown options format (e.g., "INSIDE" -> "Inside")
              const val = this.section174adjList[dd].IN_OUT_174.toString().toLowerCase();
              if (val === 'inside') {
                this.section174adjList[dd].IN_OUT_174 = 'Inside';
              } else if (val === 'outside') {
                this.section174adjList[dd].IN_OUT_174 = 'Outside';
              }
            }

            this.syncRowDropdownItems(this.section174adjList[dd]);

            this.section174adjList[dd].OLD_SECTION174_BOOK = this.section174adjList[dd].BOOK_AMT_174;
            this.section174adjList[dd].OLD_ACCT_KEY = this.section174adjList[dd].ACCT_KEY;
            // Store OLD values as empty string if null/undefined (matching original AngularJS)
            this.section174adjList[dd].OLD_SIC_CODE = this.section174adjList[dd].SIC_CODE_KEY ?? "";
            this.section174adjList[dd].OLD_IN_OUT = this.section174adjList[dd].IN_OUT_174 ?? "";
            this.section174adjList[dd].NEW_SECTION174_BOOK = this.section174adjList[dd].BOOK_AMT_174;
            this.section174adjList[dd].NEW_TAX_ADJ174 = this.section174adjList[dd].TAX_ADJ_174;
            this.section174adjList[dd].NEW_ACCT_KEY = this.section174adjList[dd].ACCT_KEY;
            this.section174adjList[dd].NEW_SIC_CODE = this.section174adjList[dd].SIC_CODE_KEY;
            this.section174adjList[dd].NEW_IN_OUT = this.section174adjList[dd].IN_OUT_174;
            // New values for temp and perm tax adj
            this.section174adjList[dd].NEW_SECTION174_TEMP_TAX_ADJ = this.section174adjList[dd].TEMP_TAX_ADJ_174;
            this.section174adjList[dd].OLD_SECTION174_TEMP_TAX_ADJ = this.section174adjList[dd].TEMP_TAX_ADJ_174;
            this.section174adjList[dd].NEW_SECTION174_PERM_TAX_ADJ = this.section174adjList[dd].PERM_TAX_ADJ_174;
            this.section174adjList[dd].OLD_SECTION174_PERM_TAX_ADJ = this.section174adjList[dd].PERM_TAX_ADJ_174;
            this.section174adjList[dd].sic_code_flag = false;
            this.section174adjList[dd].in_out_flag = false;
          }
        }

        this.sectionLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[Section174] Error loading form data:', error);
        this.sectionLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // Add new row
  addRow(row: any, index: number) {
    const row1: any = {};
    const index_position = index + 1;

    row1.type = "I";
    row1.PLUS_MINUS_FLAG = "MINUS";
    row1.BOOK_AMT_FLAG = row.BOOK_AMT_FLAG;
    row1.TEMP_TAX_AMT_FLAG = row.TEMP_TAX_AMT_FLAG;
    row1.PERM_TAX_AMT_FLAG = row.PERM_TAX_AMT_FLAG;
    row1.BOOK_AMT_174 = 0;
    row1.BOOK_AMT = 0;
    row1.TEMP_TAX_ADJ = 0;
    row1.PERM_TAX_ADJ = 0;
    row1.TEMP_TAX_ADJ_174 = 0;
    row1.PERM_TAX_ADJ_174 = 0;
    row1.ACCT = row.ACCT;
    row1.ACCT_KEY = row.ACCT_KEY;
    row1.SIC_CODE = null;
    row1.SIC_CODE_KEY = null;
    row1.IN_OUT_174 = null;
    row1.ADJ_TYPE = "";

    row1.OLD_SECTION174_BOOK = 0;
    row1.OLD_SECTION174_TEMP_TAX_ADJ = 0;
    row1.OLD_SECTION174_PERM_TAX_ADJ = 0;
    row1.OLD_ACCT_KEY = row.ACCT_KEY;
    row1.OLD_SIC_CODE = "";
    row1.OLD_IN_OUT = "";
    row1.NEW_SECTION174_BOOK = 0;
    row1.NEW_SECTION174_TEMP_TAX_ADJ = 0;
    row1.NEW_SECTION174_PERM_TAX_ADJ = 0;
    row1.NEW_ACCT_KEY = row.ACCT_KEY;
    row1.NEW_SIC_CODE = "";
    row1.NEW_IN_OUT = "";

    row1.sic_code_flag = false;
    row1.in_out_flag = false;

    this.section174adjList.splice(index_position, 0, row1);
    this.cdr.detectChanges();
  }

  // Delete row
  deleteRow(row: any) {
    const index = this.section174adjList.indexOf(row);
    if (index > -1) {
      this.section174adjList.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  // Save data
  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const sendobj: any[] = [];

    // Validate and build save data
    for (let s = 0; s < this.section174adjList.length; s++) {
      let save_acct_key = "";

      // Find SIC_CODE_KEY from selected SIC_CODE
      for (let aa in this.sic_code_all) {
        if (this.section174adjList[s].SIC_CODE === this.sic_code_all[aa].SIC_CODE_DESC) {
          save_acct_key = this.sic_code_all[aa].SIC_CODE_KEY;
        }
      }

      // Validation
      if (this.section174adjList[s].TAX_ADJ_174 !== 0 || this.section174adjList[s].BOOK_AMT_174 !== 0) {
        if (!this.section174adjList[s].SIC_CODE || !this.section174adjList[s].IN_OUT_174) {
          this._snackBar.open('Please Select SIC Code/IN OUT', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
      }

      const dummyJson: any = {};
      dummyJson.TAX_YEAR = this.globalService?.globalParams?.tax_year;
      dummyJson.SCENARIO = this.globalService?.globalParams?.scenario;
      dummyJson.JCD_KEY = this.globalService?.globalParams?.jcd_key;
      dummyJson.CHART_KEY = this.rowData.CHART_KEY;
      dummyJson.OLD_SECTION174_BOOK = Number(this.section174adjList[s].OLD_SECTION174_BOOK) || 0;
      dummyJson.NEW_SECTION174_BOOK = Number(this.section174adjList[s].BOOK_AMT_174) || 0;
      dummyJson.COMBINATION_KEY = this.rowData.COMBINATION_KEY;
      dummyJson.OLD_ACCT_KEY = this.section174adjList[s].OLD_ACCT_KEY;
      dummyJson.NEW_ACCT_KEY = this.section174adjList[s].NEW_ACCT_KEY;
      // Use empty string instead of null for OLD_* fields (matching original AngularJS)
      dummyJson.OLD_SIC_CODE = this.section174adjList[s].OLD_SIC_CODE ?? "";
      dummyJson.NEW_SIC_CODE = save_acct_key;
      dummyJson.OLD_IN_OUT = this.section174adjList[s].OLD_IN_OUT ?? "";
      dummyJson.NEW_IN_OUT = this.section174adjList[s].IN_OUT_174 ?? "";
      dummyJson.OLD_SECTION174_TEMP_TAX_ADJ = Number(this.section174adjList[s].OLD_SECTION174_TEMP_TAX_ADJ) || 0;
      dummyJson.OLD_SECTION174_PERM_TAX_ADJ = Number(this.section174adjList[s].OLD_SECTION174_PERM_TAX_ADJ) || 0;
      dummyJson.NEW_SECTION174_TEMP_TAX_ADJ = Number(this.section174adjList[s].TEMP_TAX_ADJ_174) || 0;
      dummyJson.NEW_SECTION174_PERM_TAX_ADJ = Number(this.section174adjList[s].PERM_TAX_ADJ_174) || 0;

      sendobj.push(dummyJson);
    }

    // Call save service - pass globalParams not the whole globalService
    this.section174Service.saveSection174(
      this.GENERAL_CONFIG.base_url,
      sendobj,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.callSuccess !== "1") {
          this._snackBar.open(result.errorMessage || 'Error saving data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Data has been saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  // Reset form
  reset() {
    this.isInitialized = false;
    this.isLoading = false;
    this.tryInit();
  }

  // Cancel and close modal
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  trackByIndex(index: number, item: any): number {
    return index;
  }

  onRowDropdownSelect(event: any, row: any, field: string, itemField: string): void {
    const selected = getAppkitDropdownSelection(event);
    row[itemField] = selected;
    row[field] = selected?.value ?? null;
  }

  onRowDropdownSelectItem(event: any, row: any, itemField: string): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      row[itemField] = selected;
    }
  }

  private syncRowDropdownItems(row: any): void {
    row.selectedSicItem = findAppkitItemByValue(this.sic_code_display, row.SIC_CODE);
    row.selectedInOutItem = findAppkitItemByValue(this.in_out_display, row.IN_OUT_174);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }
}
